import type { GeoCoordinates } from './assetGeo';
import {
  distanceKmBetween,
  isDefaultJerusalemCoordinates,
  parseAssetCoordinates,
} from './assetGeo';
import { assetListSortKey } from './assetListNormalize';
import { assetRecordPrimaryId } from './assetRecordPrimaryId';

function coerceNumber(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Prefer real map pins — ignore placeholder Jerusalem defaults used when geo is missing. */
export function assetRankingCoordinates(
  asset: Record<string, unknown>,
): GeoCoordinates | null {
  const coords = parseAssetCoordinates(asset);
  if (!coords || isDefaultJerusalemCoordinates(coords)) {
    return null;
  }
  return coords;
}

export function assetBookingCount(asset: Record<string, unknown>): number {
  const n = coerceNumber(
    asset.bookings_count ??
      asset.booking_count ??
      asset.bookingsCount ??
      asset.times_booked ??
      asset.timesBooked,
  );
  return n !== null && n >= 0 ? n : 0;
}

export function assetDistanceKm(
  asset: Record<string, unknown>,
  viewer: GeoCoordinates | null | undefined,
): number | null {
  if (!viewer) {
    return null;
  }
  const assetCoords = assetRankingCoordinates(asset);
  if (!assetCoords) {
    return null;
  }
  return distanceKmBetween(viewer, assetCoords);
}

/** Older assets first as a tie-break so API “newest first” order does not dominate. */
export function assetCreatedAtMs(asset: Record<string, unknown>): number | null {
  for (const key of ['registration_date', 'registered_at', 'created_at', 'createdAt'] as const) {
    const raw = asset[key];
    if (typeof raw === 'string' && raw.trim()) {
      const ms = Date.parse(raw.trim());
      if (Number.isFinite(ms)) {
        return ms;
      }
    }
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      return raw < 1e12 ? raw * 1000 : raw;
    }
  }
  return null;
}

/** Stable A–Z fallback when distance / reviews / bookings are missing. */
export function compareAssetName(a: Record<string, unknown>, b: Record<string, unknown>): number {
  const byName = assetListSortKey(a, 'en').localeCompare(assetListSortKey(b, 'en'), undefined, {
    sensitivity: 'base',
    numeric: true,
  });
  if (byName !== 0) {
    return byName;
  }
  const aId = assetRecordPrimaryId(a) ?? '';
  const bId = assetRecordPrimaryId(b) ?? '';
  return aId.localeCompare(bId);
}

export function sortAssetsAlphabetically(
  assets: Record<string, unknown>[],
): Record<string, unknown>[] {
  return [...assets].sort(compareAssetName);
}

function compareCreatedAtOldestFirst(a: Record<string, unknown>, b: Record<string, unknown>): number {
  const aMs = assetCreatedAtMs(a);
  const bMs = assetCreatedAtMs(b);
  if (aMs !== null && bMs !== null && aMs !== bMs) {
    return aMs - bMs;
  }
  if (aMs === null && bMs !== null) {
    return 1;
  }
  if (bMs === null && aMs !== null) {
    return -1;
  }
  return compareAssetName(a, b);
}

export function sortAssetsByDistance(
  assets: Record<string, unknown>[],
  viewer: GeoCoordinates | null | undefined,
): Record<string, unknown>[] {
  if (!viewer) {
    return sortAssetsByAvgRating(assets);
  }
  return [...assets].sort((a, b) => {
    const da = assetDistanceKm(a, viewer);
    const db = assetDistanceKm(b, viewer);
    if (da === null && db === null) {
      return compareCreatedAtOldestFirst(a, b);
    }
    if (da === null) {
      return 1;
    }
    if (db === null) {
      return -1;
    }
    if (da !== db) {
      return da - db;
    }
    return compareCreatedAtOldestFirst(a, b);
  });
}

/** Nearby assets: closest pool, then top ratings (review count breaks ties). */
export function pickRecommendedInAreaAssets(
  assets: Record<string, unknown>[],
  viewer: GeoCoordinates | null | undefined,
  limit: number,
): Record<string, unknown>[] {
  if (!viewer) {
    return takeTopAssets(sortAssetsByAvgRating(assets), limit);
  }
  const withDistance = sortAssetsByDistance(assets, viewer).filter(
    (asset) => assetDistanceKm(asset, viewer) !== null,
  );
  if (withDistance.length === 0) {
    return takeTopAssets(sortAssetsByAvgRating(assets), limit);
  }
  const nearbyPool = withDistance.slice(0, Math.max(limit * 8, limit));
  return takeTopAssets(sortAssetsByAvgRating(nearbyPool), limit);
}

/** Closest assets with real coordinates when location is available. */
export function pickAssetsInVicinity(
  assets: Record<string, unknown>[],
  viewer: GeoCoordinates | null | undefined,
  limit: number,
): Record<string, unknown>[] {
  if (!viewer) {
    return takeTopAssets(sortAssetsByAvgRating(assets), limit);
  }
  const withCoords = sortAssetsByDistance(assets, viewer).filter(
    (asset) => assetDistanceKm(asset, viewer) !== null,
  );
  if (withCoords.length > 0) {
    return takeTopAssets(withCoords, limit);
  }
  return takeTopAssets(sortAssetsByAvgRating(assets), limit);
}

export function sortAssetsByBookingCount(
  assets: Record<string, unknown>[],
): Record<string, unknown>[] {
  return [...assets].sort((a, b) => {
    const diff = assetBookingCount(b) - assetBookingCount(a);
    if (diff !== 0) {
      return diff;
    }
    const ratingDiff = assetAvgRating(b) - assetAvgRating(a);
    if (ratingDiff !== 0) {
      return ratingDiff;
    }
    return compareCreatedAtOldestFirst(a, b);
  });
}

export function assetReviewCount(asset: Record<string, unknown>): number {
  const n = coerceNumber(asset.reviews_count ?? asset.review_count ?? asset.reviewsCount);
  return n !== null && n >= 0 ? n : 0;
}

export function assetAvgRating(asset: Record<string, unknown>): number {
  const n = coerceNumber(asset.avg_rating ?? asset.average_rating ?? asset.avgRating ?? asset.rating);
  return n !== null && n >= 0 ? n : 0;
}

export function sortAssetsByReviewCount(
  assets: Record<string, unknown>[],
): Record<string, unknown>[] {
  return [...assets].sort((a, b) => {
    const diff = assetReviewCount(b) - assetReviewCount(a);
    if (diff !== 0) {
      return diff;
    }
    const ratingDiff = assetAvgRating(b) - assetAvgRating(a);
    if (ratingDiff !== 0) {
      return ratingDiff;
    }
    return compareCreatedAtOldestFirst(a, b);
  });
}

export function sortAssetsByAvgRating(
  assets: Record<string, unknown>[],
): Record<string, unknown>[] {
  return [...assets].sort((a, b) => {
    const ratingDiff = assetAvgRating(b) - assetAvgRating(a);
    if (ratingDiff !== 0) {
      return ratingDiff;
    }
    const reviewDiff = assetReviewCount(b) - assetReviewCount(a);
    if (reviewDiff !== 0) {
      return reviewDiff;
    }
    return compareCreatedAtOldestFirst(a, b);
  });
}

export function takeTopAssets(
  assets: Record<string, unknown>[],
  limit: number,
): Record<string, unknown>[] {
  return assets.slice(0, Math.max(0, limit));
}
